import { Alert, Box, Button, Card, CardActions, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useRef, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { routesProfile } from '../../consts'
import type { RecommendationCandidateUserMatch, RecommendationProjectMatch, RecommendationTier } from '../../api/types'
import { StyledBadge } from '../ui-kit'
import { UserAvatar } from '../common/UserAvatar'
import { USER_STATUS_OPTIONS } from '../../consts/userStatus'

const cardSx = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  maxWidth: '100%',
  minWidth: 0,
}

const sectionHeaderSx = {
  fontSize: 18,
  lineHeight: 1.25,
}

const listSx = {
  display: 'grid',
  gap: 1.5,
  minWidth: 0,
}

const truncateTextSx = {
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

export type RecommendationProjectCardMatch = RecommendationProjectMatch & {
  matches?: RecommendationProjectMatch[]
}

function tierLabel(tier: RecommendationTier): string {
  return tier === 'exact' ? 'Точное совпадение' : 'Может подойти'
}

function tierColor(tier: RecommendationTier): 'success' | 'info' {
  return tier === 'exact' ? 'success' : 'info'
}

function fieldLabel(field: string): string {
  const labels: Record<string, string> = {
    profile: 'профиль',
    competencies: 'компетенции',
    resources: 'ресурсы',
    participation: 'формат участия',
    project_need_slots: 'потребности проекта',
  }

  return labels[field] ?? field
}

const resourceAcquisitionLabels: Record<string, string> = {
  gift: 'Получить безвозмездно',
  temporary_use: 'Временно получить',
  rent: 'Арендовать',
  buy: 'Купить',
  barter: 'Обмен/бартер',
  shared_use: 'Совместно использовать',
  access: 'Доступ без передачи',
}

const resourceDurationLabels: Record<string, string> = {
  once: 'Разово',
  period: 'На период',
  permanent: 'Постоянно',
}

const resourceDeliveryLabels: Record<string, string> = {
  delivery: 'Доставка',
  pickup: 'Самовывоз',
  on_site: 'Использовать на месте',
  remote: 'Удалённо',
  discussable: 'Обсуждается',
}

const resourceBudgetLabels: Record<string, string> = {
  free: 'Бесплатно',
  has_budget: 'Есть бюджет',
  discussable: 'Обсуждается',
  barter: 'Бартер',
}

function resourceDetails(slot: RecommendationProjectMatch['slot']): string[] {
  const meta = slot.meta ?? {}

  return [
    meta.quantity ? `Количество: ${meta.quantity}` : null,
    meta.acquisition_type ? resourceAcquisitionLabels[meta.acquisition_type] ?? meta.acquisition_type : null,
    meta.duration_type ? resourceDurationLabels[meta.duration_type] ?? meta.duration_type : null,
    meta.delivery_type ? resourceDeliveryLabels[meta.delivery_type] ?? meta.delivery_type : null,
    meta.budget_type ? `Бюджет: ${resourceBudgetLabels[meta.budget_type] ?? meta.budget_type}` : null,
    meta.budget_comment ? `Комментарий: ${meta.budget_comment}` : null,
    slot.city || slot.region ? [slot.city, slot.region].filter(Boolean).join(', ') : null,
  ].filter((item): item is string => Boolean(item))
}

function SlotSummary({ slot, compact = false }: { slot: RecommendationProjectMatch['slot']; compact?: boolean }) {
  const isResource = slot.kind === 'resource'
  const groups = isResource
    ? [{ label: 'Ресурсы', names: slot.resources.map((item) => item.name).filter(Boolean) }]
    : [
      { label: 'Компетенции', names: slot.competencies.map((item) => item.name).filter(Boolean) },
      { label: 'Условия', names: slot.participation.map((item) => item.name).filter(Boolean) },
    ]
  const details = isResource
    ? resourceDetails(slot)
    : [slot.city, slot.region, slot.remote_allowed ? 'Можно удалённо' : null].filter((item): item is string => Boolean(item))

  return (
    <Stack gap={0.75}>
      <Typography variant="body2" color="text.secondary">
        {isResource ? 'Нужен ресурс' : 'Потребность'}: {slot.title}
      </Typography>
      {!compact && groups.some((group) => group.names.length > 0) && (
        <Stack gap={0.75}>
          {groups.filter((group) => group.names.length > 0).map((group) => (
            <Stack key={group.label} direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
              <Typography variant="body2" color="text.secondary">
                {group.label}:
              </Typography>
              {group.names.slice(0, 6).map((name) => (
                <Chip key={name} label={name} size="small" variant="outlined" />
              ))}
            </Stack>
          ))}
        </Stack>
      )}
      {!compact && isResource && slot.meta?.requirements && (
        <Typography variant="body2" color="text.secondary">
          {slot.meta.requirements}
        </Typography>
      )}
      {!compact && details.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {details.join(' · ')}
        </Typography>
      )}
    </Stack>
  )
}

function ProjectNeedsSummary({ match }: { match: RecommendationProjectCardMatch }) {
  const matches = match.matches ?? [match]

  if (matches.length === 1) {
    return <SlotSummary slot={match.slot} />
  }

  return (
    <Stack gap={0.75}>
      <Typography variant="body2" color="text.secondary">
        Подходит под потребности: {matches.length}
      </Typography>
      <Stack gap={0.5}>
        {matches.slice(0, 5).map((item) => (
          <Stack key={item.slot.id} direction="row" gap={0.75} alignItems="center" flexWrap="wrap" minWidth={0}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
              {item.slot.title}
            </Typography>
            <Chip label={`${item.score}%`} size="small" variant="outlined" color={tierColor(item.tier)} />
          </Stack>
        ))}
      </Stack>
      {matches.length > 5 && (
        <Typography variant="body2" color="text.secondary">
          И ещё {matches.length - 5}
        </Typography>
      )}
    </Stack>
  )
}

function joinNames(items: { name: string }[] = [], limit = 3): string {
  return items.map((item) => item.name).filter(Boolean).slice(0, limit).join('; ')
}

export function RecommendationSection<T>({ title, items, renderItem }: { title: string; items: T[]; renderItem: (item: T) => ReactNode }) {
  if (items.length === 0) {
    return null
  }

  return (
    <Stack gap={1.5}>
      <Typography variant="h2" sx={sectionHeaderSx}>
        {title}
      </Typography>
      <Box sx={listSx}>{items.map(renderItem)}</Box>
    </Stack>
  )
}

export function RecommendationsLoading() {
  return (
    <Stack alignItems="center" py={8}>
      <CircularProgress />
    </Stack>
  )
}

export function RecommendationsError() {
  return <Alert severity="error">Не удалось загрузить рекомендации.</Alert>
}

export function RecommendationsEmpty({ message }: { message: string }) {
  return (
    <Box py={7}>
      <Typography variant="h3" color="text.secondary" textAlign="center">
        {message}
      </Typography>
    </Box>
  )
}

export function RecommendationsLoadMoreTrigger({
  hasMore,
  isLoading,
  onLoadMore,
}: {
  hasMore: boolean
  isLoading: boolean
  onLoadMore: () => void
}) {
  const triggerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = triggerRef.current
    if (!node || !hasMore || isLoading) {
      return
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        onLoadMore()
      }
    }, { rootMargin: '360px 0px' })

    observer.observe(node)

    return () => observer.disconnect()
  }, [hasMore, isLoading, onLoadMore])

  if (!hasMore) {
    return null
  }

  return (
    <Stack ref={triggerRef} alignItems="center" py={2}>
      {isLoading ? <CircularProgress size={24} /> : null}
    </Stack>
  )
}

export function RecommendationProjectCard({
  match,
  onDismiss,
  isDismissing,
  primaryAction,
}: {
  match: RecommendationProjectCardMatch
  onDismiss: () => void
  isDismissing: boolean
  primaryAction?: ReactNode
}) {
  const navigate = useNavigate()
  const matches = match.matches ?? [match]
  const description = match.project.about?.description
  const needsLabel = matches.length === 1 ? 'Подходит под одну потребность' : `Подходит под ${matches.length} потребности`
  const openProject = () => {
    if (match.project.slug) {
      navigate(`/${routesProfile.PROJECTS.path}/${match.project.slug}`)
    }
  }

  return (
    <Card
      variant="primaryCard"
      sx={{ ...cardSx, cursor: 'pointer', overflow: 'hidden' }}
      onClick={openProject}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack gap={1.5} minWidth={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5} minWidth={0}>
            <Stack gap={0.75} minWidth={0} flex={1}>
              <Typography variant="h3" sx={{ fontSize: 18, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                {match.project.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={truncateTextSx} noWrap>
                {match.project.location || 'Локация не указана'}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {description}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ flexShrink: 0, maxWidth: '100%' }}>
              <Chip label={`${match.score}%`} size="small" color={tierColor(match.tier)} />
              <Chip label={tierLabel(match.tier)} size="small" variant="outlined" color={tierColor(match.tier)} />
            </Stack>
          </Stack>

          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.25 }}>
            <Stack gap={1}>
              <Typography variant="body2" color="text.secondary">
                {needsLabel}
              </Typography>
              {match.reasons.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Почему подходит: {match.reasons.slice(0, 3).join(', ')}
                </Typography>
              )}
            </Stack>
          </Box>
          <ProjectNeedsSummary match={match} />
          {match.missing_fields.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Точность зависит от заполнения: {match.missing_fields.map(fieldLabel).join(', ')}
            </Typography>
          )}
        </Stack>
      </CardContent>
      <CardActions
        sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}
        onClick={(event) => event.stopPropagation()}
      >
        {primaryAction}
        <Button
          variant="follow"
          size="small"
          disabled={isDismissing}
          onClick={onDismiss}
        >
          Скрыть
        </Button>
      </CardActions>
    </Card>
  )
}

export function RecommendationUserCard({
  match,
  onDismiss,
  isDismissing,
  primaryAction,
}: {
  match: RecommendationCandidateUserMatch
  onDismiss: () => void
  isDismissing: boolean
  primaryAction?: ReactNode
}) {
  const navigate = useNavigate()
  const user = match.user
  const isResource = match.slot.kind === 'resource'
  const displayName = user?.fullname || user?.shortname || 'Пользователь'
  const statusOption = USER_STATUS_OPTIONS.find((option) => String(option.value) === user?.status)
  const statusLabel = statusOption?.label ?? user?.status
  const locationAge = [user?.location, user?.age ? `${user.age} лет` : null].filter(Boolean).join(', ')
  const primaryMatches = isResource ? joinNames(user?.resources) : joinNames(user?.competencies)
  const participationMatches = !isResource ? joinNames(user?.participation, 2) : ''
  const navigateToUser = () => {
    if (user?.slug) {
      navigate(`/${routesProfile.USERS.path}/${user.slug}`)
    }
  }

  return (
    <Card
      sx={{ ...cardSx, overflow: 'hidden', cursor: user?.slug ? 'pointer' : 'default' }}
      onClick={navigateToUser}
    >
      <CardContent sx={{ p: 2, minWidth: 0, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" gap={{ xs: 1.5, sm: 2 }} alignItems="flex-start" minWidth={0}>
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            invisible={!user?.is_online}
          >
            <UserAvatar src={user?.avatar ?? null} size={72} />
          </StyledBadge>

          <Stack gap={0.75} minWidth={0} flex={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} minWidth={0}>
              <Typography variant="h3" sx={{ fontSize: 18, lineHeight: 1.25, width: '100%', ...truncateTextSx }} noWrap>
                {displayName}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ ml: { sm: 'auto' }, flexShrink: 0, maxWidth: '100%' }}>
                <Chip label={`${match.score}%`} size="small" color={tierColor(match.tier)} />
                <Chip label={tierLabel(match.tier)} size="small" variant="outlined" color={tierColor(match.tier)} />
              </Stack>
            </Stack>

            {(locationAge || statusLabel) && (
              <Typography variant="body2" color="text.secondary" sx={truncateTextSx} noWrap>
                {[locationAge, statusLabel].filter(Boolean).join(' · ')}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary" sx={truncateTextSx} noWrap>
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {isResource ? 'Ресурс' : 'Роль'}:{' '}
              </Box>
              {match.slot.title}
              {' · '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {match.project?.name || 'Проект'}
              </Box>
            </Typography>

            {primaryMatches && (
              <Typography variant="body2" color="text.secondary" sx={truncateTextSx} noWrap>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isResource ? 'Ресурсы: ' : 'Компетенции: '}
                </Box>
                {primaryMatches}
              </Typography>
            )}

            {participationMatches && (
              <Typography variant="body2" color="text.secondary" sx={truncateTextSx} noWrap>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Формат:{' '}
                </Box>
                {participationMatches}
              </Typography>
            )}

            {match.missing_fields.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Точность повысится, если подробнее заполнить {match.missing_fields.map(fieldLabel).join(', ')}.
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions
        sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}
        onClick={(event) => event.stopPropagation()}
      >
        {primaryAction}
        <Button
          variant="follow"
          size="small"
          disabled={isDismissing || !user}
          onClick={onDismiss}
        >
          Скрыть
        </Button>
      </CardActions>
    </Card>
  )
}

export function RecommendationResourceCard({
  match,
  onDismiss,
  isDismissing,
  primaryAction,
}: {
  match: RecommendationCandidateUserMatch
  onDismiss: () => void
  isDismissing: boolean
  primaryAction?: ReactNode
}) {
  const navigate = useNavigate()
  const user = match.user
  const providerName = user?.fullname || user?.shortname || 'Пользователь'
  const resourceNames = joinNames(match.slot.resources, 4)
  const providerResources = joinNames(user?.resources, 4)
  const details = resourceDetails(match.slot)
  const locationAge = [user?.location, user?.age ? `${user.age} лет` : null].filter(Boolean).join(', ')
  const navigateToUser = () => {
    if (user?.slug) {
      navigate(`/${routesProfile.USERS.path}/${user.slug}`)
    }
  }

  return (
    <Card
      sx={{ ...cardSx, overflow: 'hidden', cursor: user?.slug ? 'pointer' : 'default' }}
      onClick={navigateToUser}
    >
      <CardContent sx={{ p: 2, minWidth: 0, '&:last-child': { pb: 2 } }}>
        <Stack gap={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} minWidth={0}>
            <Stack gap={0.5} minWidth={0} flex={1}>
              <Typography variant="h3" sx={{ fontSize: 18, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                {match.slot.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                Нужно проекту:{' '}
                {match.project?.slug ? (
                  <Box
                    component={NavLink}
                    to={`/${routesProfile.PROJECTS.path}/${match.project.slug}`}
                    onClick={(event) => event.stopPropagation()}
                    sx={{ color: 'primary.main', textDecoration: 'none', overflowWrap: 'anywhere', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {match.project.name}
                  </Box>
                ) : (
                  match.project?.name || 'Проект'
                )}
              </Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ flexShrink: 0, maxWidth: '100%' }}>
              <Chip label={`${match.score}%`} size="small" color={tierColor(match.tier)} />
              <Chip label={tierLabel(match.tier)} size="small" variant="outlined" color={tierColor(match.tier)} />
            </Stack>
          </Stack>

          {(resourceNames || details.length > 0 || match.slot.meta?.requirements) && (
            <Stack gap={0.75}>
              {resourceNames && (
                <Typography variant="body2" color="text.secondary">
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Категория:{' '}
                  </Box>
                  {resourceNames}
                </Typography>
              )}
              {details.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {details.join(' · ')}
                </Typography>
              )}
              {match.slot.meta?.requirements && (
                <Typography variant="body2" color="text.secondary">
                  {match.slot.meta.requirements}
                </Typography>
              )}
            </Stack>
          )}

          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
            <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
                invisible={!user?.is_online}
              >
                <UserAvatar src={user?.avatar ?? null} size={48} />
              </StyledBadge>
              <Stack minWidth={0} gap={0.25} flex={1}>
                <Typography variant="body1" fontWeight={600} sx={truncateTextSx} noWrap>
                  {providerName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={truncateTextSx} noWrap>
                  {locationAge || 'Может предоставить ресурс'}
                </Typography>
                {providerResources && (
                  <Typography variant="body2" color="text.secondary" sx={truncateTextSx} noWrap>
                    {providerResources}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
      <CardActions
        sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}
        onClick={(event) => event.stopPropagation()}
      >
        {primaryAction}
        <Button
          variant="follow"
          size="small"
          disabled={isDismissing || !user}
          onClick={onDismiss}
        >
          Скрыть
        </Button>
      </CardActions>
    </Card>
  )
}
